import {
  TenderSubmissionMetadata,
  TenderSubmissionMetadata_SubmissionType,
} from "gen/txn/models_pb"
import { capitalize } from "lodash"
import { Money } from "shared-ts/classes/money"

import { NO_PAYMENT_NECESSARY_TEXT } from "./constants"
import { DisplayableTenderSubmissionMetadata } from "./makeTenderSubmissionMetadata"

export interface TenderSubmissionToTextParams {
  tsm: DisplayableTenderSubmissionMetadata
  isPaymentComplete: boolean
}

export function tenderSubmissionToText({
  tsm,
  isPaymentComplete,
}: TenderSubmissionToTextParams): string {
  const cardChargeText = isPaymentComplete ? "was charged" : "will be charged"
  const amount = Money.fromPB(tsm.amount)

  if (amount.isZero()) {
    return NO_PAYMENT_NECESSARY_TEXT
  }

  switch (tsm.submissionType) {
    case TenderSubmissionMetadata_SubmissionType.CASH: {
      const cashPaid = Money.fromPB(tsm.cash?.cashReceived)
      return `${amount.format()} was accepted via cash. ${cashPaid.format()} received, ${cashPaid
        .sub(amount as Money)
        .format()} change`
    }
    case TenderSubmissionMetadata_SubmissionType.CHECK:
      return `${amount.format()} was accepted via check`
    case TenderSubmissionMetadata_SubmissionType.SAVED_CARD: {
      const card = tsm.savedCard?.card
      if (!card) {
        return `${amount.format()} ${cardChargeText} to card`
      }
      return `${amount.format()} ${cardChargeText} to ${capitalize(
        card?.brand
      )} •••• •••• •••• ${card?.last4}`
    }
    case TenderSubmissionMetadata_SubmissionType.MANUALLY_ENTERED_CARD: {
      const last4 = tsm.manuallyEnteredCard?.card?.cardNumber?.slice(-4)
      if (!last4) {
        return `${amount.format()} ${cardChargeText} to card`
      }
      return `${amount.format()} ${cardChargeText} to •••• •••• •••• ${last4}`
    }
    case TenderSubmissionMetadata_SubmissionType.PRESENT_CARD:
      return `${amount.format()} ${cardChargeText} to card`
    case TenderSubmissionMetadata_SubmissionType.GIFT_CARD:
      return `${amount.format()} was accepted via gift card`
    case TenderSubmissionMetadata_SubmissionType.CUSTOM:
      return `${amount.format()} was accepted`
    case TenderSubmissionMetadata_SubmissionType.CHARGE_ACCOUNT:
      return `${amount.format()} was charged to account`
    default:
      return ""
  }
}

export function isCardSubmissionType(
  submissionType: TenderSubmissionMetadata_SubmissionType
): boolean {
  return (
    submissionType ===
      TenderSubmissionMetadata_SubmissionType.MANUALLY_ENTERED_CARD ||
    submissionType === TenderSubmissionMetadata_SubmissionType.PRESENT_CARD ||
    submissionType === TenderSubmissionMetadata_SubmissionType.SAVED_CARD
  )
}

export function sumTenderSubmissionAmounts(
  submissions: TenderSubmissionMetadata[]
): Money {
  let total = Money.zero()
  for (const tsm of submissions) {
    if (!tsm.amount || Money.fromPB(tsm.amount).lte(Money.zero())) {
      return Money.zero()
    }
    total = Money.fromPB(tsm.amount).add(total)
  }
  return total
}

export function calculateSurchargeFromTenderSubmissions(
  submissions: TenderSubmissionMetadata[]
): Money {
  return submissions.reduce(
    (acc, tsm) =>
      acc.add(Money.fromPB(tsm.surcharge)).add(Money.fromPB(tsm.surchargeTax)),
    Money.zero()
  )
}
